import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

/**
 * Public "client requirements" lead form — collected without login.
 *
 * Reused by the landing-page CTA, the WhatsApp inquiry survey link, and
 * any marketing campaign. On submit we POST to a public /leads/client-help
 * endpoint so admins see every inquiry alongside logged-in idea submissions.
 */
const ASSISTANCE = [
  "Topic selection",
  "Literature review",
  "Methodology",
  "Data analysis",
  "Writing & editing",
  "Target journal selection",
  "End-to-end publication",
  "Plagiarism help",
  "Other",
];
const LEVELS = ["Undergraduate", "Masters", "PhD", "Faculty", "Other"];
const CONTACT = [
  { k: "email", label: "Email" },
  { k: "whatsapp", label: "WhatsApp" },
  { k: "phone", label: "Phone call" },
];

export default function GetHelp() {
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", preferred_contact: "email",
    domain: "", assistance: "", target_journal: "", level: "",
    has_title: "no", title: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.whatsapp.trim())) {
      toast.error("Share your name and at least one contact (email or WhatsApp)."); return;
    }
    setBusy(true);
    try {
      await api.post("/leads/client-help", form);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't submit. Try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <SEO
        title="Get research help in Nepal — KhojByte Research Lab"
        description="Tell us your research goals and we'll match you with the right mentor. Paper help, thesis-to-paper conversion, methodology and more — no sign-up needed."
      />
      <Navbar />
      <main className="min-h-screen bg-kb-bg" data-testid="get-help-page">
        <section className="max-w-3xl mx-auto px-5 py-14">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">GET STARTED</div>
          <h1 className="font-serif text-3xl lg:text-5xl text-kb-ink mt-2 leading-tight">
            Tell us what <em className="italic text-kb-primary">research help</em> you need.
          </h1>
          <p className="text-sm lg:text-base text-kb-ink-muted mt-3 max-w-xl">
            One short form, no account required. We'll reply within 24 hours via the channel you prefer.
          </p>

          {sent ? (
            <div className="mt-10 kb-card p-10 text-center" data-testid="get-help-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" strokeWidth={1.5} />
              <h2 className="font-serif text-2xl text-kb-ink mt-3">Thanks — we'll be in touch soon.</h2>
              <p className="text-sm text-kb-ink-muted mt-2">A KhojByte mentor will reach out via your preferred channel within one working day.</p>
              <div className="mt-6 flex items-center gap-2 justify-center">
                <Link to="/" className="kb-btn-outline text-sm">Back to home</Link>
                <Link to="/research-ideas" className="kb-btn-primary text-sm">Browse curated ideas <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 kb-card p-8 space-y-5" data-testid="get-help-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Full name *</label>
                  <input required className="kb-input mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="get-help-name" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Email</label>
                  <input type="email" className="kb-input mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="get-help-email" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">WhatsApp number</label>
                  <input type="tel" className="kb-input mt-1.5" placeholder="+977 98xxxxxxxx" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="get-help-whatsapp" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Preferred contact</label>
                  <div className="mt-1.5 inline-flex rounded-sm border border-slate-200 overflow-hidden">
                    {CONTACT.map((c) => (
                      <button
                        type="button"
                        key={c.k}
                        onClick={() => setForm({ ...form, preferred_contact: c.k })}
                        className={`px-3 py-1.5 kb-mono text-[10px] uppercase tracking-wider border-r border-slate-200 last:border-r-0 ${form.preferred_contact === c.k ? "bg-kb-primary text-white" : "text-kb-ink-muted hover:bg-slate-50"}`}
                        data-testid={`get-help-contact-${c.k}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Domain / research area *</label>
                  <input required className="kb-input mt-1.5" placeholder="e.g. Machine Learning, Climate, Public Health" value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} data-testid="get-help-domain" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Type of assistance *</label>
                  <select required className="kb-input mt-1.5" value={form.assistance} onChange={(e) => setForm({ ...form, assistance: e.target.value })} data-testid="get-help-assistance">
                    <option value="">Choose…</option>
                    {ASSISTANCE.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Target journal (optional)</label>
                  <input className="kb-input mt-1.5" value={form.target_journal} onChange={(e) => setForm({ ...form, target_journal: e.target.value })} data-testid="get-help-journal" />
                </div>
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Educational level (optional)</label>
                  <select className="kb-input mt-1.5" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="get-help-level">
                    <option value="">—</option>
                    {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Already have a title / topic?</label>
                <div className="mt-1.5 flex items-center gap-4 text-sm">
                  <label className="inline-flex items-center gap-1.5"><input type="radio" name="has_title" checked={form.has_title === "yes"} onChange={() => setForm({ ...form, has_title: "yes" })} data-testid="get-help-has-title-yes" /> Yes</label>
                  <label className="inline-flex items-center gap-1.5"><input type="radio" name="has_title" checked={form.has_title === "no"} onChange={() => setForm({ ...form, has_title: "no" })} data-testid="get-help-has-title-no" /> No</label>
                </div>
                {form.has_title === "yes" && (
                  <input className="kb-input mt-2" placeholder="Your working title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="get-help-title" />
                )}
              </div>

              <div>
                <label className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">Anything else we should know? (optional)</label>
                <textarea rows={3} className="kb-input mt-1.5 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="get-help-notes" placeholder="Deadline, data you already have, specific concerns…" />
              </div>

              <div className="flex items-center justify-between pt-2 gap-3 flex-wrap">
                <div className="text-xs text-kb-ink-muted inline-flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5 text-kb-primary" /> Reply within 1 working day.</div>
                <button type="submit" disabled={busy} className="kb-btn-primary" data-testid="get-help-submit">
                  {busy ? "Submitting…" : (<>Send inquiry <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}
